// src/components/PatientDetails/panels/EligibleTrialsPanel.tsx
import {
  CheckCircle,
  Cancel,
  Close,
  HelpOutline,
  LocationOn,
  OpenInNew,
  PersonAdd,
  PlaylistAdd,
  Science,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import React, { useCallback, useState } from "react";
import ExpandToggle from "../../layout/ExpandToggle";
import GlassBox from "../../layout/GlassBox";
import {
  useTrialMatches,
  type TrialMatch,
  type TrialLocation,
  type EligibilityVerdict,
  type EvaluationMeta,
} from "../../../hooks/useTrialMatches";
import { useOrganization } from "../../../hooks/useBilling";
import { useTranslation } from "../../../i18n";

const PHASE_LABELS: Record<string, string> = {
  early_phase_1: "Early Phase 1",
  phase_1: "Phase I",
  phase_1_2: "Phase I/II",
  phase_2: "Phase II",
  phase_2_3: "Phase II/III",
  phase_3: "Phase III",
  phase_4: "Phase IV",
  not_applicable: "N/A",
};

function verdictConfig(verdict: EligibilityVerdict, theme: Theme, t: (key: string, fallback: string) => string) {
  switch (verdict) {
    case "likely":
      return {
        label: t("trials.likely_eligible", "Likely Eligible"),
        icon: <CheckCircle sx={{ fontSize: 13 }} />,
        color: theme.palette.success.main,
      };
    case "unlikely":
      return {
        label: t("trials.unlikely", "Unlikely"),
        icon: <Cancel sx={{ fontSize: 13 }} />,
        color: theme.palette.error.main,
      };
    default:
      return {
        label: t("trials.criteria_unclear", "Unclear"),
        icon: <HelpOutline sx={{ fontSize: 13 }} />,
        color: theme.palette.warning.main,
      };
  }
}

interface Props {
  patientId: number | undefined;
  onEnrollmentCreated?: () => void;
  autoSearch?: boolean;
}

export default function EligibleTrialsPanel({
  patientId,
  onEnrollmentCreated,
  autoSearch,
}: Props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const { matches, loading, error, warning, hasSearched, evaluationMeta, searchParams, findMatches, enrollPatient } =
    useTrialMatches(patientId);
  const [enrollingNct, setEnrollingNct] = useState<string | null>(null);
  const autoFired = React.useRef(false);

  // Location filter — pre-populated from org default, editable per-session
  const { organization } = useOrganization();
  const orgDefault = organization?.default_trial_location || "";
  const [location, setLocation] = useState("");
  const [editingLocation, setEditingLocation] = useState(false);
  // Whether the USER has set this location, as opposed to it being the org
  // default or the value echoed back from the last run. It matters because an
  // empty location is ambiguous: "I never touched it" must keep falling back to
  // the org default, while "I cleared it" must mean WORLDWIDE. Without this
  // distinction, clearing the chip sends nothing, the backend re-applies
  // `organization.default_trial_location`, and the country reappears.
  const [locationTouched, setLocationTouched] = useState(false);

  // Sync from org default when it arrives (async fetch)
  const orgDefaultApplied = React.useRef(false);
  React.useEffect(() => {
    if (orgDefault && !orgDefaultApplied.current) {
      orgDefaultApplied.current = true;
      setLocation(orgDefault);
    }
  }, [orgDefault]);

  // cursor_logs/048 (Design B): after a run, reflect the EFFECTIVE location the
  // matcher used into the box. On the first search this surfaces the location
  // the model inferred (e.g. "Germany") so it's visible — not hidden behind a
  // misleading "Worldwide" — and the user can then clear it (→ worldwide) or
  // change it and Re-run. While the user is actively editing the box we don't
  // clobber their input.
  React.useEffect(() => {
    if (
      searchParams &&
      typeof searchParams.location_term === "string" &&
      !editingLocation
    ) {
      setLocation(searchParams.location_term);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const locationArg = locationTouched ? location : location || undefined;



  // Editable CT.gov query (cursor_logs/048). The three fields a clinician most
  // often needs to loosen on a 0-result run; the rest (age_group, study_type,
  // sort_order, …) are preserved verbatim from the last run, and location is
  // owned by the dedicated field above. Edits prefill from the last run's
  // effective params and reset whenever a fresh run comes back.
  const PARAM_FIELDS = React.useMemo(
    () =>
      [
        ["condition", t("trials.param_condition", "Condition")],
        ["general_term", t("trials.param_term", "Term")],
        ["overall_status", t("trials.param_status", "Status")],
      ] as const,
    [t],
  );
  const [paramEdits, setParamEdits] = useState<Record<string, string>>({});
  React.useEffect(() => {
    if (searchParams) {
      setParamEdits({
        condition: searchParams.condition || "",
        general_term: searchParams.general_term || "",
        overall_status: searchParams.overall_status || "",
      });
    }
  }, [searchParams]);

  // The effective CT.gov query: the last run's params with the user's edits
  // applied. Sending it makes the backend run those params VERBATIM instead of
  // re-deriving them with the LLM, which is what keeps a cleared location
  // meaning worldwide rather than the model's inferred country.
  const buildSearchParams = useCallback(() => {
    const base: Record<string, string> = { ...(searchParams || {}) };
    delete base.location_term; // location is owned by the location field
    for (const [key] of PARAM_FIELDS) {
      const v = (paramEdits[key] ?? "").trim();
      if (v) base[key] = v;
      else delete base[key];
    }
    return base;
  }, [paramEdits, searchParams, PARAM_FIELDS]);

  // The FIRST search has no params to preserve and must let the LLM derive the
  // query. Every search after it carries the effective params, otherwise the
  // backend re-derives them and the user's edits and cleared location are
  // silently replaced by the model's inference. Enforced here rather than at
  // each call site, because the four entry points (Re-run, Retry, Re-evaluate,
  // Enter-in-the-location-box) all need it and one of them was missing it.
  const runSearch = useCallback(() => {
    findMatches({
      location: locationArg,
      ...(searchParams ? { search_params: buildSearchParams() } : {}),
    });
  }, [findMatches, locationArg, searchParams, buildSearchParams]);

  // "Search more" means MORE OF THE SAME QUERY. It used to send no
  // search_params, so the LLM re-derived the query on every press: the criteria
  // could drift, and a cleared location was re-filled with the model's inferred
  // country, silently undoing a worldwide search mid-pagination.
  const handleSearchMore = useCallback(() => {
    const existingIds = matches.map((m) => m.nct_id);
    findMatches({
      location: locationArg,
      search_params: buildSearchParams(),
      exclude_nct_ids: existingIds,
      append: true,
    });
  }, [findMatches, locationArg, buildSearchParams, matches]);

  React.useEffect(() => {
    if (
      autoSearch &&
      patientId &&
      !hasSearched &&
      !loading &&
      !autoFired.current
    ) {
      autoFired.current = true;
      runSearch();
    }
  }, [autoSearch, patientId, hasSearched, loading, runSearch]);

  const handleEnroll = useCallback(
    async (nctId: string) => {
      setEnrollingNct(nctId);
      try {
        await enrollPatient(nctId);
        onEnrollmentCreated?.();
      } catch {
        // Error is logged inside the hook
      } finally {
        setEnrollingNct(null);
      }
    },
    [enrollPatient, onEnrollmentCreated]
  );

  const likelyCount = matches.filter((m) => m.eligible === "likely").length;
  const headerBadgeCount = hasSearched && matches.length > 0 ? matches.length : 0;

  const containerSx = {
    overflowX: "auto" as const,
    px: 1,
    borderRadius: theme.shape.borderRadius / 6,
    background: `linear-gradient(180deg,
      ${alpha(theme.palette.primary.dark, 0.1)} 0%,
      ${alpha(theme.palette.primary.dark, 0.1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.secondary.main, 0.55)}`,
    backdropFilter: "blur(8px)",
    "&::-webkit-scrollbar": { width: 3, height: 3 },
    "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(120,120,140,0.18)",
      borderRadius: 2,
    },
    scrollbarWidth: "thin" as const,
    scrollbarColor: "rgba(120,120,140,0.18) transparent",
  };

  return (
    <Box>
      <GlassBox
        variant="filled"
        expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {t("trials.eligible_title", "Eligible Clinical Trials")}
          </Typography>

          {/* Location chip — always visible in header */}
          {editingLocation ? (
            <TextField
              size="small"
              autoFocus
              placeholder={t("trials.location_placeholder", "City, Country (or leave blank for worldwide)")}
              value={location}
              onChange={(e) => {
                setLocation(e.target.value);
                setLocationTouched(true);
              }}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                  setEditingLocation(false);
                  if (hasSearched) runSearch();
                }
                if (e.key === "Escape") setEditingLocation(false);
              }}
              onBlur={() => setEditingLocation(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LocationOn sx={{ fontSize: 14, opacity: 0.5 }} />
                  </InputAdornment>
                ),
                endAdornment: location ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                      e.stopPropagation();
                      setLocation("");
                      setLocationTouched(true);
                      setEditingLocation(false);
                    }}
                      sx={{ p: 0.25 }}
                    >
                      <Close sx={{ fontSize: 12 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{
                width: 210,
                "& .MuiOutlinedInput-root": { fontSize: "0.75rem", height: 26 },
              }}
            />
          ) : (
            <Tooltip title={t("trials.change_location_tooltip", "Click to change location filter")} arrow placement="top">
              <Chip
                icon={<LocationOn sx={{ fontSize: 13 }} />}
                label={location || t("trials.worldwide", "Worldwide")}
                size="small"
                onClick={(e) => { e.stopPropagation(); setEditingLocation(true); }}
                onDelete={
                  location
                    ? (e) => {
                        e.stopPropagation();
                        setLocation("");
                        setLocationTouched(true);
                      }
                    : undefined
                }
                deleteIcon={location ? <Close sx={{ fontSize: 11 }} /> : undefined}
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  fontWeight: 700,
                  cursor: "pointer",
                  bgcolor: alpha(theme.palette.secondary.main, 0.25),
                  color: theme.palette.common.white,
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
                  "& .MuiChip-icon": { color: theme.palette.common.white },
                  "& .MuiChip-deleteIcon": { color: alpha(theme.palette.common.white, 0.7) },
                }}
              />
            </Tooltip>
          )}

          {headerBadgeCount > 0 && (
            <Chip
              label={
                likelyCount > 0
                  ? `${likelyCount} likely / ${headerBadgeCount}`
                  : headerBadgeCount
              }
              size="small"
              sx={{
                fontWeight: 700,
                height: 20,
                fontSize: "0.7rem",
                bgcolor: alpha(theme.palette.secondary.main, 0.25),
                color: theme.palette.common.white,
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
              }}
            />
          )}

          {headerBadgeCount > 0 && !loading && (
            <Chip
              icon={loading ? undefined : <PlaylistAdd sx={{ fontSize: 13 }} />}
              label={t("trials.search_more", "Search More")}
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleSearchMore();
              }}
              sx={{
                height: 20,
                fontSize: "0.7rem",
                fontWeight: 700,
                cursor: "pointer",
                bgcolor: alpha(theme.palette.info.main, 0.2),
                color: theme.palette.common.white,
                border: `1px solid ${alpha(theme.palette.info.main, 0.5)}`,
                "& .MuiChip-icon": { color: theme.palette.common.white },
                "&:hover": {
                  bgcolor: alpha(theme.palette.info.main, 0.35),
                },
              }}
            />
          )}
          {headerBadgeCount > 0 && loading && (
            <CircularProgress size={14} sx={{ color: theme.palette.info.main }} />
          )}
        </Stack>
        <ExpandToggle expanded={expanded} />
      </GlassBox>

      <Collapse in={expanded}>
        <Box sx={{ mt: 1 }}>
          {/* Editable CT.gov query (cursor_logs/048): shows the exact search the
              matcher ran and lets the clinician loosen it + re-run — the fix for
              a silently over-narrow query (e.g. an LLM-inferred location). */}
          {searchParams && (
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                gap: 0.75,
                mb: 1.25,
                px: 0.75,
                py: 0.75,
                borderRadius: 1,
                bgcolor: alpha(theme.palette.secondary.main, 0.06),
                border: `1px solid ${alpha(theme.palette.secondary.main, 0.25)}`,
              }}
            >
              <Tooltip
                title={t(
                  "trials.search_query_tooltip",
                  "The query sent to ClinicalTrials.gov. Edit and re-run to widen or refine the search.",
                )}
                arrow
                placement="top"
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, color: theme.palette.primary.dark }}
                >
                  {t("trials.search_query", "Query")}
                </Typography>
              </Tooltip>
              {PARAM_FIELDS.map(([key, label]) => (
                <TextField
                  key={key}
                  size="small"
                  label={label}
                  value={paramEdits[key] ?? ""}
                  onChange={(e) =>
                    setParamEdits((p) => ({ ...p, [key]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter") runSearch();
                  }}
                  InputLabelProps={{ shrink: true, sx: { fontSize: "0.7rem" } }}
                  sx={{
                    width: key === "condition" ? 190 : 150,
                    "& .MuiOutlinedInput-root": {
                      fontSize: "0.72rem",
                      height: 28,
                    },
                  }}
                />
              ))}
              <Button
                size="small"
                variant="outlined"
                onClick={runSearch}
                disabled={loading || !patientId}
                startIcon={<Science sx={{ fontSize: 14 }} />}
                sx={{
                  textTransform: "none",
                  fontSize: "0.72rem",
                  minHeight: 28,
                  py: 0,
                  borderColor: alpha(theme.palette.secondary.main, 0.5),
                  color: theme.palette.primary.dark,
                  "&:hover": {
                    borderColor: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  },
                }}
              >
                {t("trials.rerun", "Re-run")}
              </Button>
            </Box>
          )}

          {!hasSearched && !loading && (
            <Box sx={{ textAlign: "center", py: 2 }}>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: theme.palette.primary.dark }}
              >
                {t("trials.empty_title", "Find recruiting clinical trials that match this patient's profile.")}
              </Typography>

              <Button
                variant="outlined"
                size="small"
                startIcon={<Science sx={{ fontSize: 16 }} />}
                onClick={runSearch}
                disabled={!patientId}
                sx={{
                  textTransform: "none",
                  fontSize: "0.8rem",
                  borderColor: alpha(theme.palette.secondary.main, 0.5),
                  color: theme.palette.primary.dark,
                  "&:hover": {
                    borderColor: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  },
                }}
              >
                {t("trials.find_matches", "Find Matches")}
              </Button>
            </Box>
          )}

          {loading && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <CircularProgress
                size={20}
                sx={{ color: theme.palette.primary.dark }}
              />
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: theme.palette.primary.dark,
                  fontSize: "0.8rem",
                }}
              >
                {t("trials.analyzing", "Analysing patient profile and matching against ClinicalTrials.gov...")}
              </Typography>
              {warning && (
                <Alert severity="warning" sx={{ mt: 1.5, fontSize: "0.75rem", textAlign: "left" }}>
                  {warning}
                </Alert>
              )}
            </Box>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 1, fontSize: "0.8rem" }}>
              {error}
            </Alert>
          )}

          {evaluationMeta &&
            !loading &&
            evaluationMeta.assessmentsReturned < evaluationMeta.candidatesEvaluated && (
              <Alert
                severity="warning"
                sx={{ mb: 1, fontSize: "0.75rem" }}
                action={
                  <Button
                    size="small"
                    onClick={runSearch}
                    sx={{ textTransform: "none", fontSize: "0.7rem" }}
                  >
                    {t("trials.re_evaluate", "Re-evaluate")}
                  </Button>
                }
              >
                {t("trials.incomplete_warning", "{{count}} of {{total}} trials could not be fully evaluated. Results may be incomplete.")
                  .replace("{{count}}", String(evaluationMeta.candidatesEvaluated - evaluationMeta.assessmentsReturned))
                  .replace("{{total}}", String(evaluationMeta.candidatesEvaluated))}
              </Alert>
            )}

          {hasSearched && !loading && matches.length === 0 && (
            <Box sx={{ textAlign: "center", py: 3 }}>
              <Typography
                variant="body2"
                sx={{
                  color: theme.palette.primary.dark,
                  fontSize: "0.8rem",
                }}
              >
                {t("trials.no_matches", "No matching trials found.")}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{
                  mt: 1,
                  textTransform: "none",
                  fontSize: "0.75rem",
                  borderColor: alpha(theme.palette.secondary.main, 0.5),
                  color: theme.palette.primary.dark,
                  "&:hover": {
                    borderColor: theme.palette.secondary.main,
                    bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  },
                }}
                // This is the same action as Re-run, offered on the empty
                // state. It used to be a separate handler that sent no
                // search_params, so it DISCARDED the user's Condition / Term /
                // Status edits and let the LLM re-derive the query it had just
                // failed with.
                onClick={runSearch}
              >
                {t("trials.retry", "Retry")}
              </Button>
            </Box>
          )}

          {matches.length > 0 && (
            <Box sx={containerSx}>
              {matches.map((match) => (
                <TrialMatchCard
                  key={match.nct_id}
                  match={match}
                  onEnroll={handleEnroll}
                  enrolling={enrollingNct === match.nct_id}
                  theme={theme}
                  searchedLocation={location}
                />
              ))}
            </Box>
          )}

          {hasSearched && !loading && matches.length > 0 && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              sx={{ pt: 1, pb: 0.5 }}
            >
              <Button
                variant="text"
                size="small"
                onClick={runSearch}
                sx={{
                  textTransform: "none",
                  fontSize: "0.75rem",
                  color: theme.palette.primary.dark,
                }}
              >
                {t("trials.refresh", "Refresh Matches")}
              </Button>
            </Stack>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Trial card
// ---------------------------------------------------------------------------

function TrialMatchCard({
  match,
  onEnroll,
  enrolling,
  theme,
  searchedLocation,
}: {
  match: TrialMatch;
  onEnroll: (nctId: string) => void;
  enrolling: boolean;
  theme: Theme;
  searchedLocation?: string;
}) {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);
  const verdict = verdictConfig(match.eligible, theme, t);
  const confidencePct = Math.round(match.confidence * 100);

  return (
    <Box
      sx={{
        py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      {/* Row 1: NCT ID, phase, eligibility verdict, confidence */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <Box sx={{ flex: 1, mr: 1 }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 0.25 }}
          >
            <Link
              href={match.url}
              target="_blank"
              rel="noopener"
              underline="hover"
              variant="caption"
              sx={{
                fontWeight: 600,
                fontFamily: "monospace",
                fontSize: "0.75rem",
                color: theme.palette.primary.dark,
              }}
            >
              {match.nct_id}
              <OpenInNew
                sx={{ fontSize: 10, ml: 0.3, verticalAlign: "middle" }}
              />
            </Link>
            <Chip
              label={PHASE_LABELS[match.phase] || match.phase || "—"}
              size="small"
              variant="outlined"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                borderColor: alpha(theme.palette.secondary.main, 0.4),
                color: theme.palette.primary.dark,
              }}
            />
            {match.is_enrolled && (
              <Chip
                icon={<CheckCircle sx={{ fontSize: 12 }} />}
                label={t("trials.enrolled", "Enrolled")}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  bgcolor: alpha(theme.palette.success.main, 0.15),
                  color: theme.palette.success.main,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                }}
              />
            )}
          </Stack>

          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              lineHeight: 1.3,
              fontSize: "0.8rem",
              color: theme.palette.primary.dark,
            }}
          >
            {match.brief_title}
          </Typography>
          {match.sponsor && (
            <Typography
              variant="caption"
              sx={{
                color: alpha(theme.palette.primary.dark, 0.6),
                fontSize: "0.7rem",
              }}
            >
              {match.sponsor}
            </Typography>
          )}
          <TrialSites
            locations={match.locations}
            searchedLocation={searchedLocation}
            theme={theme}
          />
        </Box>

        {/* Eligibility badge */}
        <Tooltip
          title={t("trials.confidence", "{{percent}}% confidence").replace("{{percent}}", String(confidencePct))}
          placement="left"
          arrow
        >
          <Chip
            icon={verdict.icon}
            label={verdict.label}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.7rem",
              fontWeight: 600,
              bgcolor: alpha(verdict.color, 0.12),
              color: verdict.color,
              border: `1px solid ${alpha(verdict.color, 0.35)}`,
              "& .MuiChip-icon": { color: verdict.color },
              flexShrink: 0,
            }}
          />
        </Tooltip>
      </Stack>

      {/* Row 2: Matching feature chips — why this trial matched */}
      <MatchingChips match={match} theme={theme} />

      {/* Row 3: LLM reasoning summary */}
      {match.reasoning && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            color: alpha(theme.palette.primary.dark, 0.7),
            fontSize: "0.72rem",
            fontStyle: "italic",
            lineHeight: 1.4,
          }}
        >
          {match.reasoning}
        </Typography>
      )}

      {/* Row 4: Actions */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{ mt: 0.75 }}
        alignItems="center"
      >
        {!match.is_enrolled && (
          <Button
            variant="outlined"
            size="small"
            startIcon={
              enrolling ? (
                <CircularProgress size={12} />
              ) : (
                <PersonAdd sx={{ fontSize: 14 }} />
              )
            }
            onClick={() => onEnroll(match.nct_id)}
            disabled={enrolling}
            sx={{
              textTransform: "none",
              fontSize: "0.7rem",
              py: 0.25,
              borderColor: alpha(theme.palette.secondary.main, 0.4),
              color: theme.palette.primary.dark,
              "&:hover": {
                borderColor: theme.palette.secondary.main,
                bgcolor: alpha(theme.palette.secondary.main, 0.08),
              },
            }}
          >
            {enrolling ? t("trials.enrolling", "Enrolling...") : t("trials.enroll", "Enroll")}
          </Button>
        )}
        <Button
          variant="text"
          size="small"
          onClick={() => setDetailOpen((s) => !s)}
          sx={{
            textTransform: "none",
            fontSize: "0.7rem",
            py: 0.25,
            color: theme.palette.primary.dark,
          }}
        >
          {detailOpen ? t("trials.hide_details", "Hide Details") : t("trials.show_details", "Details")}
        </Button>
      </Stack>

      {/* Expandable detail: met / unmet / unclear + raw criteria */}
      <Collapse in={detailOpen}>
        <CriteriaDetail match={match} theme={theme} />
      </Collapse>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Matching feature chips — distills *why* the trial matched
// ---------------------------------------------------------------------------

function MatchingChips({
  match,
  theme,
}: {
  match: TrialMatch;
  theme: Theme;
}) {
  const chips: { label: string; color: string }[] = [];

  for (const criterion of match.met_criteria.slice(0, 5)) {
    chips.push({ label: criterion, color: theme.palette.success.main });
  }

  if (match.conditions?.length) {
    const condLabel =
      match.conditions.length <= 2
        ? match.conditions.join(", ")
        : `${match.conditions[0]} +${match.conditions.length - 1}`;
    chips.push({ label: condLabel, color: theme.palette.info.main });
  }

  if (chips.length === 0) return null;

  return (
    <Stack
      direction="row"
      sx={{ mt: 0.5, flexWrap: "wrap", gap: 0.4 }}
    >
      {chips.map((chip, i) => (
        <Chip
          key={i}
          label={chip.label}
          size="small"
          sx={{
            height: 20,
            fontSize: "0.65rem",
            fontWeight: 500,
            maxWidth: 220,
            bgcolor: alpha(chip.color, 0.1),
            color: chip.color,
            border: `1px solid ${alpha(chip.color, 0.3)}`,
          }}
        />
      ))}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Trial sites — compact location display with highlighted matching city
// ---------------------------------------------------------------------------

function TrialSites({
  locations,
  searchedLocation,
  theme,
}: {
  locations: TrialLocation[];
  searchedLocation?: string;
  theme: Theme;
}) {
  const { t } = useTranslation();
  if (!locations || locations.length === 0) return null;

  const tokens = (searchedLocation || "")
    .split(",")
    .map((tok) => tok.trim().toLowerCase())
    .filter(Boolean);

  const isMatch = (loc: TrialLocation) =>
    tokens.length > 0 &&
    tokens.some(
      (tok) =>
        (loc.city || "").toLowerCase().includes(tok) ||
        (loc.country || "").toLowerCase().includes(tok)
    );

  const sorted = [...locations].sort((a, b) => {
    const aMatch = isMatch(a) ? 0 : 1;
    const bMatch = isMatch(b) ? 0 : 1;
    return aMatch - bMatch;
  });

  const MAX_SHOWN = 3;
  const shown = sorted.slice(0, MAX_SHOWN);
  const remaining = locations.length - MAX_SHOWN;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ mt: 0.3, flexWrap: "wrap", gap: 0.3 }}
    >
      <LocationOn
        sx={{ fontSize: 12, color: alpha(theme.palette.primary.dark, 0.4) }}
      />
      {shown.map((loc, i) => {
        const highlight = isMatch(loc);
        const label = [loc.city, loc.country].filter(Boolean).join(", ");
        return (
          <Typography
            key={i}
            variant="caption"
            sx={{
              fontSize: "0.65rem",
              fontWeight: highlight ? 600 : 400,
              color: highlight
                ? theme.palette.info.main
                : alpha(theme.palette.primary.dark, 0.5),
            }}
          >
            {label}
            {i < shown.length - 1 || remaining > 0 ? " · " : ""}
          </Typography>
        );
      })}
      {remaining > 0 && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.63rem",
            color: alpha(theme.palette.primary.dark, 0.4),
          }}
        >
          {t("trials.more_locations", "+{{count}} more").replace("{{count}}", String(remaining))}
        </Typography>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Criteria detail (met / unmet / unclear lists + raw eligibility text)
// ---------------------------------------------------------------------------

function CriteriaDetail({
  match,
  theme,
}: {
  match: TrialMatch;
  theme: Theme;
}) {
  const { t } = useTranslation();
  const sections: {
    items: string[];
    color: string;
    icon: React.ReactNode;
    label: string;
  }[] = [
    {
      items: match.met_criteria,
      color: theme.palette.success.main,
      icon: <CheckCircle sx={{ fontSize: 11 }} />,
      label: t("trials.criteria_met", "Met"),
    },
    {
      items: match.unmet_criteria,
      color: theme.palette.error.main,
      icon: <Cancel sx={{ fontSize: 11 }} />,
      label: t("trials.criteria_unmet", "Unmet"),
    },
    {
      items: match.unclear_criteria,
      color: theme.palette.warning.main,
      icon: <HelpOutline sx={{ fontSize: 11 }} />,
      label: t("trials.criteria_unclear", "Unclear"),
    },
  ];

  const hasCriteria = sections.some((s) => s.items.length > 0);

  return (
    <Box
      sx={{
        mt: 0.75,
        p: 1,
        borderRadius: theme.shape.borderRadius / 6,
        background: `linear-gradient(180deg,
          ${alpha(theme.palette.primary.dark, 0.06)} 0%,
          ${alpha(theme.palette.primary.dark, 0.04)} 100%)`,
        border: `1px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
        maxHeight: 260,
        overflow: "auto",
        "&::-webkit-scrollbar": { width: 3 },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: "rgba(120,120,140,0.18)",
          borderRadius: 2,
        },
        scrollbarWidth: "thin" as const,
        scrollbarColor: "rgba(120,120,140,0.18) transparent",
      }}
    >
      {hasCriteria && (
        <Stack spacing={0.75} sx={{ mb: 1 }}>
          {sections.map(
            (section) =>
              section.items.length > 0 && (
                <Box key={section.label}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.3}
                    sx={{ mb: 0.25 }}
                  >
                    <Box sx={{ color: section.color, display: "flex" }}>
                      {section.icon}
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.68rem",
                        color: section.color,
                      }}
                    >
                      {section.label} ({section.items.length})
                    </Typography>
                  </Stack>
                  <Stack spacing={0.15} sx={{ pl: 1.5 }}>
                    {section.items.map((item, idx) => (
                      <Typography
                        key={idx}
                        variant="caption"
                        sx={{
                          fontSize: "0.68rem",
                          lineHeight: 1.35,
                          color: alpha(theme.palette.primary.dark, 0.8),
                        }}
                      >
                        • {item}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )
          )}
        </Stack>
      )}

      {match.eligibility_criteria && (
        <>
          {hasCriteria && (
            <Box
              sx={{
                borderTop: `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                pt: 0.75,
                mt: 0.5,
              }}
            />
          )}
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              fontSize: "0.68rem",
              color: alpha(theme.palette.primary.dark, 0.7),
              display: "block",
              mb: 0.25,
            }}
          >
            {t("trials.full_criteria", "Full Eligibility Criteria")}
          </Typography>
          <Typography
            variant="caption"
            component="pre"
            sx={{
              whiteSpace: "pre-wrap",
              fontFamily: "inherit",
              lineHeight: 1.5,
              m: 0,
              fontSize: "0.68rem",
              color: alpha(theme.palette.primary.dark, 0.65),
            }}
          >
            {match.eligibility_criteria}
          </Typography>
        </>
      )}

      {!hasCriteria && !match.eligibility_criteria && (
        <Typography
          variant="caption"
          sx={{
            fontSize: "0.7rem",
            color: alpha(theme.palette.primary.dark, 0.5),
          }}
        >
          {t("trials.no_criteria", "No eligibility criteria available.")}
        </Typography>
      )}
    </Box>
  );
}
